import { create } from "zustand";
import type { ChatMessage, ChatTurn } from "../types/ai.js";
import type { JsonPatch } from "../types/ai.js";
import { useEditorStore } from "./editorStore.js";

type AIStore = {
  isPanelOpen: boolean;
  panelWidth: number;
  isStreaming: boolean;
  messages: ChatMessage[];
  pendingPatch: JsonPatch[] | null;
  patchSummary: string | null;
  /** チャット編集の対話ヒアリング往復履歴（ai-template-generation.md §6）。ステートレス送信のため毎回全文送る */
  conversation: ChatTurn[];
  openPanel: () => void;
  closePanel: () => void;
  setPanelWidth: (width: number) => void;
  setStreaming: (value: boolean) => void;
  addMessage: (message: Omit<ChatMessage, "id" | "createdAt">) => void;
  setPendingPatch: (patch: JsonPatch[] | null, summary?: string | null) => void;
  applyPatch: () => void;
  rejectPatch: () => void;
  clearMessages: () => void;
  addConversationTurn: (turn: ChatTurn) => void;
  clearConversation: () => void;
};

function messageId(): string {
  return `msg_${Math.random().toString(36).slice(2, 10)}`;
}

export const useAIStore = create<AIStore>((set, get) => ({
  isPanelOpen: false,
  panelWidth: 390,
  isStreaming: false,
  messages: [],
  pendingPatch: null,
  patchSummary: null,
  conversation: [],

  openPanel: () => set({ isPanelOpen: true }),
  closePanel: () => set({ isPanelOpen: false }),
  setPanelWidth: (width) => set({ panelWidth: width }),
  setStreaming: (value) => set({ isStreaming: value }),
  addMessage: (message) => set((state) => ({
    messages: [...state.messages, { ...message, id: messageId(), createdAt: new Date().toISOString() }],
  })),
  setPendingPatch: (patch, summary = null) => set({ pendingPatch: patch, patchSummary: summary }),
  applyPatch: () => {
    const patch = get().pendingPatch;
    if (!patch) return;
    useEditorStore.getState().applyPatch(patch);
    set({ pendingPatch: null, patchSummary: null });
  },
  rejectPatch: () => set({ pendingPatch: null, patchSummary: null }),
  clearMessages: () => set({ messages: [], pendingPatch: null, patchSummary: null, conversation: [] }),
  addConversationTurn: (turn) => set((state) => ({ conversation: [...state.conversation, turn] })),
  // patch を受けたら（= ヒアリング/編集ループが完結したら）往復履歴をクリアする
  clearConversation: () => set({ conversation: [] }),
}));
